import { useState, useCallback } from 'react';
import { Button } from '@/components/ui/Button';
import { Badge, type BadgeVariant } from '@/components/ui/Badge';
import { Card } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Modal } from '@/components/ui/Modal';
import { Tabs, TabPanel, useTabs } from '@/components/ui/Tabs';
import {
  useEnvelopesList,
  useEnvelope,
  useEnvelopeEvents,
  useCreateEnvelope,
  useVoidEnvelope,
  useRemindEnvelope,
  useESignatureConfig,
  useUpdateESignatureConfig,
  type Envelope,
  type EnvelopeStatus,
  type EnvelopesListParams,
  type CreateEnvelopeRequest,
  type EnvelopeEvent,
} from './hooks/useESignature';

// ─── Constants ───────────────────────────────────────────────────────────────

const STATUS_VARIANTS: Record<EnvelopeStatus, BadgeVariant> = {
  draft: 'default',
  sent: 'info',
  delivered: 'info',
  completed: 'success',
  declined: 'danger',
  voided: 'warning',
  expired: 'default',
};

const STATUS_LABELS: Record<EnvelopeStatus, string> = {
  draft: 'Draft',
  sent: 'Sent',
  delivered: 'Delivered',
  completed: 'Completed',
  declined: 'Declined',
  voided: 'Voided',
  expired: 'Expired',
};

const STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'sent', label: 'Sent' },
  { value: 'delivered', label: 'Delivered' },
  { value: 'completed', label: 'Completed' },
  { value: 'declined', label: 'Declined' },
  { value: 'voided', label: 'Voided' },
];

const TABS = [
  { id: 'envelopes', label: 'Envelopes' },
  { id: 'config', label: 'Configuration' },
];

// ─── Sub-components ──────────────────────────────────────────────────────────

interface SignerFormEntry {
  name: string;
  email: string;
  order: number;
}

function CreateEnvelopeForm({ onSuccess }: { onSuccess: () => void }) {
  const createEnvelope = useCreateEnvelope();
  const [documentId, setDocumentId] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [signers, setSigners] = useState<SignerFormEntry[]>([{ name: '', email: '', order: 1 }]);

  const addSigner = useCallback(() => {
    setSigners((prev) => [...prev, { name: '', email: '', order: prev.length + 1 }]);
  }, []);

  const removeSigner = useCallback((index: number) => {
    setSigners((prev) => {
      const updated = prev.filter((_, i) => i !== index);
      return updated.map((s, i) => ({ ...s, order: i + 1 }));
    });
  }, []);

  const updateSigner = useCallback((index: number, field: keyof SignerFormEntry, value: string) => {
    setSigners((prev) => prev.map((s, i) => (i === index ? { ...s, [field]: value } : s)));
  }, []);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      const payload: CreateEnvelopeRequest = {
        documentId,
        signers: signers.map((s) => ({
          name: s.name,
          email: s.email,
          order: s.order,
        })),
        dueDate: dueDate || undefined,
      };
      await createEnvelope.mutateAsync(payload);
      onSuccess();
    },
    [documentId, dueDate, signers, createEnvelope, onSuccess],
  );

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label
          htmlFor="documentId"
          className="block text-sm font-medium text-gray-700 dark:text-gray-300"
        >
          Document ID
        </label>
        <Input
          id="documentId"
          placeholder="Enter document ID to sign"
          value={documentId}
          onChange={(e) => setDocumentId(e.target.value)}
          required
        />
      </div>

      <div>
        <label
          htmlFor="dueDate"
          className="block text-sm font-medium text-gray-700 dark:text-gray-300"
        >
          Due Date
        </label>
        <Input
          id="dueDate"
          type="date"
          value={dueDate}
          onChange={(e) => setDueDate(e.target.value)}
        />
      </div>

      <fieldset>
        <legend className="text-sm font-medium text-gray-700 dark:text-gray-300">Signers</legend>
        <div className="mt-2 space-y-3">
          {signers.map((signer, index) => (
            <div key={index} className="flex items-center gap-2">
              <span className="flex h-6 w-6 items-center justify-center rounded-full bg-brand-100 text-xs font-medium text-brand-700 dark:bg-brand-900 dark:text-brand-300">
                {signer.order}
              </span>
              <Input
                placeholder="Name"
                value={signer.name}
                onChange={(e) => updateSigner(index, 'name', e.target.value)}
                required
                aria-label={`Signer ${signer.order} name`}
              />
              <Input
                placeholder="Email"
                type="email"
                value={signer.email}
                onChange={(e) => updateSigner(index, 'email', e.target.value)}
                required
                aria-label={`Signer ${signer.order} email`}
              />
              {signers.length > 1 && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => removeSigner(index)}
                  aria-label={`Remove signer ${signer.order}`}
                >
                  <svg
                    className="h-4 w-4 text-gray-400 hover:text-danger-500"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </Button>
              )}
            </div>
          ))}
        </div>
        <Button type="button" variant="outline" size="sm" className="mt-2" onClick={addSigner}>
          Add Signer
        </Button>
      </fieldset>

      <div className="flex justify-end gap-2 pt-2">
        <Button type="submit" disabled={createEnvelope.isPending}>
          {createEnvelope.isPending ? 'Sending...' : 'Send for Signing'}
        </Button>
      </div>

      {createEnvelope.isError && (
        <p className="text-sm text-danger-600 dark:text-danger-400" role="alert">
          Failed to create envelope. Please try again.
        </p>
      )}
    </form>
  );
}

function EnvelopeDetail({ envelopeId, onClose }: { envelopeId: string; onClose: () => void }) {
  const { data: envelope, isLoading } = useEnvelope(envelopeId);
  const { data: events } = useEnvelopeEvents(envelopeId);
  const voidEnvelope = useVoidEnvelope();
  const remindEnvelope = useRemindEnvelope();
  const [showVoidConfirm, setShowVoidConfirm] = useState(false);
  const [voidReason, setVoidReason] = useState('');

  const handleVoid = useCallback(async () => {
    await voidEnvelope.mutateAsync({ id: envelopeId, reason: voidReason });
    setShowVoidConfirm(false);
    setVoidReason('');
  }, [envelopeId, voidReason, voidEnvelope]);

  const handleRemind = useCallback(async () => {
    await remindEnvelope.mutateAsync(envelopeId);
  }, [envelopeId, remindEnvelope]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8" role="status">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-brand-500 border-t-transparent" />
        <span className="sr-only">Loading envelope details</span>
      </div>
    );
  }

  if (!envelope) {
    return <p className="py-4 text-sm text-gray-500 dark:text-gray-400">Envelope not found.</p>;
  }

  const canVoid = ['sent', 'delivered'].includes(envelope.status);
  const canRemind = ['sent', 'delivered'].includes(envelope.status);

  return (
    <div className="space-y-6">
      {/* Status and actions */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            {envelope.documentName}
          </h3>
          <Badge variant={STATUS_VARIANTS[envelope.status]}>{STATUS_LABELS[envelope.status]}</Badge>
        </div>
        <div className="flex gap-2">
          {canRemind && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleRemind}
              disabled={remindEnvelope.isPending}
            >
              {remindEnvelope.isPending ? 'Sending...' : 'Send Reminder'}
            </Button>
          )}
          {canVoid && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowVoidConfirm(true)}
              className="text-danger-600 hover:text-danger-700"
            >
              Void
            </Button>
          )}
          {envelope.signedDocumentUrl && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => window.open(envelope.signedDocumentUrl!, '_blank')}
            >
              Download Signed
            </Button>
          )}
        </div>
      </div>

      {/* Signers */}
      <Card padding="sm">
        <h4 className="mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">Signers</h4>
        <div className="space-y-2">
          {envelope.signers.map((signer) => (
            <div
              key={signer.id}
              className="flex items-center justify-between rounded-md bg-gray-50 px-3 py-2 dark:bg-gray-800"
            >
              <div className="flex items-center gap-2">
                <span className="flex h-5 w-5 items-center justify-center rounded-full bg-brand-100 text-xs font-medium text-brand-700 dark:bg-brand-900 dark:text-brand-300">
                  {signer.order}
                </span>
                <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  {signer.name}
                </span>
                <span className="text-sm text-gray-500 dark:text-gray-400">({signer.email})</span>
              </div>
              <Badge
                variant={
                  signer.status === 'signed'
                    ? 'success'
                    : signer.status === 'declined'
                      ? 'danger'
                      : 'default'
                }
                size="sm"
              >
                {signer.status}
              </Badge>
            </div>
          ))}
        </div>
      </Card>

      {/* Timeline */}
      {events && events.length > 0 && (
        <div>
          <h4 className="mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
            Status Timeline
          </h4>
          <div className="space-y-2">
            {events.map((event: EnvelopeEvent) => (
              <div
                key={event.id}
                className="flex items-start gap-3 border-l-2 border-gray-200 pl-4 dark:border-gray-700"
              >
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-gray-900 dark:text-gray-100">{event.description}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {new Date(event.occurredAt).toLocaleString()}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Void confirmation */}
      {showVoidConfirm && (
        <Card padding="sm" className="border-danger-200 dark:border-danger-800">
          <p className="mb-2 text-sm font-medium text-danger-700 dark:text-danger-400">
            Are you sure you want to void this envelope?
          </p>
          <Input
            placeholder="Reason for voiding"
            value={voidReason}
            onChange={(e) => setVoidReason(e.target.value)}
            required
          />
          <div className="mt-2 flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setShowVoidConfirm(false)}>
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={handleVoid}
              disabled={!voidReason || voidEnvelope.isPending}
              className="bg-danger-600 hover:bg-danger-700"
            >
              {voidEnvelope.isPending ? 'Voiding...' : 'Confirm Void'}
            </Button>
          </div>
        </Card>
      )}

      <div className="flex justify-end">
        <Button variant="outline" onClick={onClose}>
          Close
        </Button>
      </div>
    </div>
  );
}

function ConfigurationPanel() {
  const { data: config, isLoading } = useESignatureConfig();
  const updateConfig = useUpdateESignatureConfig();
  const [accountId, setAccountId] = useState('');
  const [integrationKey, setIntegrationKey] = useState('');

  const handleSave = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      await updateConfig.mutateAsync({
        accountId: accountId || undefined,
        integrationKey: integrationKey || undefined,
      });
    },
    [accountId, integrationKey, updateConfig],
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8" role="status">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-brand-500 border-t-transparent" />
        <span className="sr-only">Loading configuration</span>
      </div>
    );
  }

  return (
    <Card padding="md">
      <h3 className="mb-4 text-lg font-semibold text-gray-900 dark:text-gray-100">
        DocuSign Connection Settings
      </h3>

      {config?.isConnected && (
        <div className="mb-4 flex items-center gap-2">
          <Badge variant="success" dot>
            Connected
          </Badge>
          {config.lastSyncAt && (
            <span className="text-xs text-gray-500 dark:text-gray-400">
              Last sync: {new Date(config.lastSyncAt).toLocaleString()}
            </span>
          )}
        </div>
      )}

      {!config?.isConnected && (
        <div className="mb-4">
          <Badge variant="warning" dot>
            Not Connected
          </Badge>
        </div>
      )}

      <form onSubmit={handleSave} className="space-y-4">
        <div>
          <label
            htmlFor="accountId"
            className="block text-sm font-medium text-gray-700 dark:text-gray-300"
          >
            Account ID
          </label>
          <Input
            id="accountId"
            placeholder={config?.accountId || 'Enter DocuSign Account ID'}
            value={accountId}
            onChange={(e) => setAccountId(e.target.value)}
          />
        </div>

        <div>
          <label
            htmlFor="integrationKey"
            className="block text-sm font-medium text-gray-700 dark:text-gray-300"
          >
            Integration Key
          </label>
          <Input
            id="integrationKey"
            type="password"
            placeholder="Enter integration key"
            value={integrationKey}
            onChange={(e) => setIntegrationKey(e.target.value)}
          />
        </div>

        <div className="flex justify-end">
          <Button type="submit" disabled={updateConfig.isPending}>
            {updateConfig.isPending ? 'Saving...' : 'Save Configuration'}
          </Button>
        </div>

        {updateConfig.isSuccess && (
          <p className="text-sm text-success-600 dark:text-success-400" role="status">
            Configuration saved successfully.
          </p>
        )}
        {updateConfig.isError && (
          <p className="text-sm text-danger-600 dark:text-danger-400" role="alert">
            Failed to save configuration.
          </p>
        )}
      </form>
    </Card>
  );
}

// ─── Main Page Component ─────────────────────────────────────────────────────

/**
 * E-Signature page with envelope management and DocuSign configuration.
 * Supports creating envelopes, tracking status, sending reminders, and voiding.
 * Validates: Requirements 20.1, 20.2
 */
export function ESignaturePage() {
  const { activeTab, onChange: setActiveTab } = useTabs('envelopes');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedEnvelopeId, setSelectedEnvelopeId] = useState<string | null>(null);
  const [params, setParams] = useState<EnvelopesListParams>({
    page: 1,
    pageSize: 25,
    status: '',
    search: '',
  });

  const { data, isLoading } = useEnvelopesList(params);

  return (
    <div className="flex h-full flex-col">
      {/* Page header */}
      <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4 dark:border-gray-700">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">E-Signatures</h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Send documents for electronic signing via DocuSign
          </p>
        </div>
        <Button onClick={() => setShowCreateModal(true)}>New Envelope</Button>
      </div>

      {/* Tabs */}
      <div className="px-6 pt-4">
        <Tabs tabs={TABS} activeTab={activeTab} onChange={setActiveTab} />
      </div>

      {/* Envelopes Tab */}
      <TabPanel tabId="envelopes" activeTab={activeTab}>
        <div className="px-6">
          {/* Filters */}
          <div className="mb-4 flex flex-wrap items-center gap-4">
            <div className="w-48">
              <Input
                placeholder="Search envelopes..."
                value={params.search}
                onChange={(e) =>
                  setParams((prev) => ({ ...prev, search: e.target.value, page: 1 }))
                }
              />
            </div>
            <div className="w-40">
              <Select
                label=""
                options={STATUS_OPTIONS}
                value={params.status ?? ''}
                onChange={(e) =>
                  setParams((prev) => ({
                    ...prev,
                    status: e.target.value as EnvelopeStatus | '',
                    page: 1,
                  }))
                }
              />
            </div>
          </div>

          {/* Loading */}
          {isLoading && (
            <div className="flex items-center justify-center py-12" role="status">
              <div className="h-8 w-8 animate-spin rounded-full border-2 border-brand-500 border-t-transparent" />
              <span className="sr-only">Loading envelopes</span>
            </div>
          )}

          {/* Empty state */}
          {!isLoading && (!data?.items || data.items.length === 0) && (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <svg
                className="mb-4 h-12 w-12 text-gray-300 dark:text-gray-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                No e-signature envelopes yet
              </p>
              <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">
                Create an envelope to send a document for signing
              </p>
            </div>
          )}

          {/* Envelopes table */}
          {data?.items && data.items.length > 0 && (
            <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
              <table className="w-full text-left text-sm">
                <thead className="border-b border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800">
                  <tr>
                    <th className="px-4 py-3 font-medium text-gray-700 dark:text-gray-300">
                      Document
                    </th>
                    <th className="px-4 py-3 font-medium text-gray-700 dark:text-gray-300">
                      Status
                    </th>
                    <th className="px-4 py-3 font-medium text-gray-700 dark:text-gray-300">
                      Signers
                    </th>
                    <th className="px-4 py-3 font-medium text-gray-700 dark:text-gray-300">
                      Due Date
                    </th>
                    <th className="px-4 py-3 font-medium text-gray-700 dark:text-gray-300">Sent</th>
                    <th className="px-4 py-3 font-medium text-gray-700 dark:text-gray-300">
                      <span className="sr-only">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {data.items.map((envelope: Envelope) => (
                    <tr
                      key={envelope.id}
                      className="cursor-pointer bg-white hover:bg-gray-50 dark:bg-gray-900 dark:hover:bg-gray-800"
                      onClick={() => setSelectedEnvelopeId(envelope.id)}
                    >
                      <td className="px-4 py-3">
                        <span className="font-medium text-gray-900 dark:text-gray-100">
                          {envelope.documentName}
                        </span>
                        {envelope.clientName && (
                          <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">
                            ({envelope.clientName})
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <Badge variant={STATUS_VARIANTS[envelope.status]} size="sm">
                          {STATUS_LABELS[envelope.status]}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-gray-600 dark:text-gray-400">
                        {envelope.signers.length} signer
                        {envelope.signers.length !== 1 ? 's' : ''}
                      </td>
                      <td className="px-4 py-3 text-gray-600 dark:text-gray-400">
                        {envelope.dueDate ? new Date(envelope.dueDate).toLocaleDateString() : '—'}
                      </td>
                      <td className="px-4 py-3 text-gray-600 dark:text-gray-400">
                        {envelope.sentAt ? new Date(envelope.sentAt).toLocaleDateString() : '—'}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedEnvelopeId(envelope.id);
                          }}
                          aria-label={`View envelope ${envelope.documentName}`}
                        >
                          View
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {data && data.total > data.pageSize && (
            <div className="mt-4 flex items-center justify-between">
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Showing {(data.page - 1) * data.pageSize + 1} to{' '}
                {Math.min(data.page * data.pageSize, data.total)} of {data.total} envelopes
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={data.page <= 1}
                  onClick={() => setParams((prev) => ({ ...prev, page: (prev.page ?? 1) - 1 }))}
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={data.page * data.pageSize >= data.total}
                  onClick={() => setParams((prev) => ({ ...prev, page: (prev.page ?? 1) + 1 }))}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </div>
      </TabPanel>

      {/* Configuration Tab */}
      <TabPanel tabId="config" activeTab={activeTab}>
        <div className="px-6">
          <ConfigurationPanel />
        </div>
      </TabPanel>

      {/* Create Envelope Modal */}
      <Modal
        open={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title="Send Document for Signing"
        size="lg"
      >
        <CreateEnvelopeForm onSuccess={() => setShowCreateModal(false)} />
      </Modal>

      {/* Envelope Detail Modal */}
      <Modal
        open={!!selectedEnvelopeId}
        onClose={() => setSelectedEnvelopeId(null)}
        title="Envelope Details"
        size="xl"
      >
        {selectedEnvelopeId && (
          <EnvelopeDetail
            envelopeId={selectedEnvelopeId}
            onClose={() => setSelectedEnvelopeId(null)}
          />
        )}
      </Modal>
    </div>
  );
}
